import React, { memo, useEffect, useMemo, useState } from 'react';
import { useParams, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import dayjs from 'utils/dayjs';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { usePageSchema } from 'hooks/usePageSchema';

import Page from 'components/Page';
import PageHelmet from 'components/PageHelmet';
import PageInner from 'components/PageInner';
import NbuPageContent from 'components/NbuPageContent';
import PageSidebar from 'components/PageSidebar';
import LayoutContainer from 'components/LayoutContainer';
import NbuPageFooter from 'components/NbuPageFooter';

import { fetchNbuCurr } from 'actions';

import {
  DEFAULT_API_DATE_FORMAT,
  parseDate,
  shouldNavigateToCurrencyYearArchive,
} from 'utils';

import { currencies } from 'constants/currency';

import { Content, Wrapper } from './styles';

const filterByWhiteList = (data, activeKey) => {
  const keys = activeKey ? [activeKey] : Object.values(currencies);
  const whiteList = new Map(keys.map(item => [item, item]));

  return data.filter(el => whiteList.has(el?.code.toLowerCase()));
};

const NbuPage = memo(({ location, staticContext }) => {
  const params = useParams();
  const intl = useIntl();
  const dispatch = useDispatch();

  const { locale } = useIntl();
  const [date, setDate] = useState(params.date);

  const data = useSelector(state =>
    filterByWhiteList(state.currency.nbuCurrencyList.data, params.currency)
  );

  useEffect(() => {
    if (params.date !== date) {
      setDate(params.date);
    }
  }, [params.date]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const urlParams = useMemo(() => {
    return {
      date: (parseDate(date) || dayjs()).format(DEFAULT_API_DATE_FORMAT),
      locale,
    };
  }, [date, locale]);

  useEffect(() => {
    dispatch(fetchNbuCurr(urlParams));
  }, [dispatch, urlParams]);

  if (shouldNavigateToCurrencyYearArchive(params.date)) {
    if (staticContext) {
      staticContext.status = 404;
    }

    return null;
  }

  if (staticContext && !data) {
    staticContext.status = 404;
  }

  const mainEntityName = intl.formatMessage({
    id: `nbuRates`,
  });

  const defaultMetaTitle = intl.formatMessage({
    id: `homePage.metaTitle`,
  });

  const defaultMetaDescription = intl.formatMessage({
    id: `homePage.metaDescription`,
  });

  const {
    pageScripts,
    formattedMetaTitle,
    formattedMetaDescription,
  } = usePageSchema({
    location,
    mainEntity: data,
    mainEntityName,
    defaultMetaTitle,
    defaultMetaDescription,
  });

  return (
    <Page>
      <Wrapper>
        <LayoutContainer>
          <PageHelmet
            title={formattedMetaTitle}
            description={formattedMetaDescription}
            script={pageScripts}
          />
        </LayoutContainer>
      </Wrapper>
      <Content>
        <LayoutContainer>
          <PageInner>
            <NbuPageContent />
            <PageSidebar />
          </PageInner>
          <NbuPageFooter />
        </LayoutContainer>
      </Content>
    </Page>
  );
});

NbuPage.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
  staticContext: PropTypes.objectOf(PropTypes.any),
};

NbuPage.defaultProps = {
  staticContext: null,
};

export default withRouter(NbuPage);
